import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import CustomInputField from "../../components/ui/inputs/CustomInputField";
import { usePhoneCountryCodes } from "../../hooks/userComplementaryQuery";
import { 
  EPS_OPTIONS, 
  searchInOptions, 
  getPaginatedOptions,
  DEFAULT_DROPDOWN_CONFIG 
} from "../../lib/userProfileData";
import SortDropdown from '../../components/ui/dropdawn/SortDropdown';
import "../../assets/scss/components/user/ContactInfoEdit.scss";

const ContactInfoEdit = ({
  contactInfo,
  systemInfo, // Para manejar EPS y ARL
  onContactInfoChange,
  onSystemInfoChange,
  theme
}) => {
  // Hooks para obtener datos complementarios
  const { data: countryCodes, isLoading: isLoadingCountryCodes } = usePhoneCountryCodes();
  
  // Estados para manejo de búsquedas en dropdowns
  const [epsSearch, setEpsSearch] = useState({
    searchTerm: '',
    currentPage: 1,
    isLoading: false
  });

  // Preparar datos para dropdowns de países
  const countryCodesData = useMemo(() => {
    if (!countryCodes?.data) return [];
    return countryCodes.data.map(country => ({
      value: country.id,
      label: country.country_name,
      country_code: country.country_code
    }));
  }, [countryCodes]);

  // Configuración para dropdown de EPS con búsqueda
  const epsOptions = useMemo(() => {
    const { options } = getPaginatedOptions(
      EPS_OPTIONS,
      epsSearch.currentPage,
      DEFAULT_DROPDOWN_CONFIG.itemsPerPage,
      epsSearch.searchTerm
    );
    return options;
  }, [epsSearch.searchTerm, epsSearch.currentPage]);

  const epsTotalItems = useMemo(() => {
    return searchInOptions(EPS_OPTIONS, epsSearch.searchTerm).length;
  }, [epsSearch.searchTerm]);

  // Handlers para campos de contacto
  const handleContactChange = useCallback((field, value) => {
    onContactInfoChange(field, value);
  }, [onContactInfoChange]);

  const handleSystemChange = useCallback((field, value) => {
    onSystemInfoChange(field, value);
  }, [onSystemInfoChange]);

  // Handlers para búsqueda en EPS
  const handleEpsSearch = useCallback((searchTerm) => {
    setEpsSearch(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1,
      isLoading: false
    }));
  }, []);

  const handleEpsPageChange = useCallback((page) => {
    setEpsSearch(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  // Validación de teléfono (solo números)
  const handlePhoneChange = useCallback((field, value) => {
    // Permitir solo números, espacios, guiones y paréntesis
    const phoneRegex = /^[0-9\s\-\(\)]*$/;
    if (phoneRegex.test(value) || value === '') {
      handleContactChange(field, value);
    }
  }, [handleContactChange]);

  return (
    <div className={`GM__${theme}-contact-info-edit`}>
      <div className={`GM__${theme}-contact-content`}>
        
        {/* Sección de información de contacto principal */}
        <div className={`GM__${theme}-primary-contact-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información de Contacto Principal
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            {/* Email */}
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field-full`}>
                <label htmlFor="email" className={`GM__${theme}-field-label`}>
                  Correo Electrónico *
                </label>
                <CustomInputField
                  id="email"
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  placeholder="usuario@ejemplo.com"
                  required
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>

            {/* Teléfono principal */}
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="primaryPhoneCountryId" className={`GM__${theme}-field-label`}>
                  País (Teléfono Principal)
                </label>
                <SortDropdown
                  value={contactInfo.primaryPhoneCountryId || ""}
                  onChange={(value) => handleContactChange('primaryPhoneCountryId', value)}
                  options={countryCodesData}
                  type="phone"
                  theme={theme}
                  placeholder="Seleccione país"
                  isLoading={isLoadingCountryCodes}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="primaryPhoneNumber" className={`GM__${theme}-field-label`}>
                  Número de Teléfono Principal
                </label>
                <CustomInputField
                  id="primaryPhoneNumber"
                  value={contactInfo.primaryPhoneNumber}
                  onChange={(e) => handlePhoneChange('primaryPhoneNumber', e.target.value)}
                  placeholder="300 123 4567"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>

            {/* Teléfono secundario */}
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="secondaryPhoneCountryId" className={`GM__${theme}-field-label`}>
                  País (Teléfono Secundario)
                </label>
                <SortDropdown
                  value={contactInfo.secondaryPhoneCountryId || ""}
                  onChange={(value) => handleContactChange('secondaryPhoneCountryId', value)}
                  options={countryCodesData}
                  type="phone"
                  theme={theme}
                  placeholder="Seleccione país"
                  isLoading={isLoadingCountryCodes}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="secondaryPhoneNumber" className={`GM__${theme}-field-label`}>
                  Número de Teléfono Secundario
                </label>
                <CustomInputField
                  id="secondaryPhoneNumber"
                  value={contactInfo.secondaryPhoneNumber}
                  onChange={(e) => handlePhoneChange('secondaryPhoneNumber', e.target.value)}
                  placeholder="601 234 5678"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sección de contacto de emergencia */}
        <div className={`GM__${theme}-emergency-contact-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Contacto de Emergencia
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="emergencyContactName" className={`GM__${theme}-field-label`}>
                  Nombre Completo del Contacto de Emergencia
                </label>
                <CustomInputField
                  id="emergencyContactName"
                  value={contactInfo.emergencyContactName}
                  onChange={(e) => handleContactChange('emergencyContactName', e.target.value)}
                  placeholder="Ej. María Elena Rodríguez Pérez"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="emergencyContactPhone" className={`GM__${theme}-field-label`}>
                  Teléfono del Contacto de Emergencia
                </label>
                <CustomInputField
                  id="emergencyContactPhone"
                  value={contactInfo.emergencyContactPhone}
                  onChange={(e) => handlePhoneChange('emergencyContactPhone', e.target.value)}
                  placeholder="300 987 6543"
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sección de información de salud y seguridad social */}
        <div className={`GM__${theme}-health-info-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información de Salud y Seguridad Social
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="eps" className={`GM__${theme}-field-label`}>
                  EPS (Entidad Promotora de Salud)
                </label>
                <SortDropdown
                  value={systemInfo.eps || ""}
                  onChange={(value) => handleSystemChange('eps', value)}
                  options={epsOptions}
                  placeholder="Seleccione EPS"
                  theme={theme}
                  enableSearch={true}
                  enablePagination={true}
                  onSearch={handleEpsSearch}
                  searchPlaceholder="Buscar EPS..."
                  itemsPerPage={DEFAULT_DROPDOWN_CONFIG.itemsPerPage}
                  totalItems={epsTotalItems}
                  currentPage={epsSearch.currentPage}
                  onPageChange={handleEpsPageChange}
                  searchTerm={epsSearch.searchTerm}
                  isLoading={epsSearch.isLoading}
                  className={`GM__${theme}-dropdown-field`}
                />
              </div>
              <div className={`GM__${theme}-field`}>
                <label htmlFor="arl" className={`GM__${theme}-field-label`}>
                  ARL (Administradora de Riesgos Laborales)
                </label>
                <CustomInputField
                  id="arl"
                  value={systemInfo.arl}
                  onChange={(e) => handleSystemChange('arl', e.target.value)}
                  placeholder="Ej. SURA ARL, Positiva, AXA Colpatria, etc."
                  theme={theme}
                  className={`GM__${theme}-input-field`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sección de información adicional */}
        <div className={`GM__${theme}-additional-info-section`}>
          <h4 className={`GM__${theme}-subsection-title`}>
            Información Adicional
          </h4>
          <div className={`GM__${theme}-info-grid`}>
            <div className={`GM__${theme}-field-group`}>
              <div className={`GM__${theme}-field-full`}>
                <div className={`GM__${theme}-info-note`}>
                  <p className={`GM__${theme}-note-text`}>
                    <strong>Nota:</strong> La información de contacto de emergencia es importante para 
                    casos de accidentes o situaciones que requieran comunicación inmediata con un familiar 
                    o persona cercana.
                  </p>
                  <p className={`GM__${theme}-note-text`}>
                    Los datos de EPS y ARL son fundamentales para el cumplimiento de las obligaciones 
                    de seguridad social y atención médica en caso de ser necesario.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ContactInfoEdit.propTypes = {
  contactInfo: PropTypes.shape({
    primaryPhoneNumber: PropTypes.string,
    primaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    secondaryPhoneNumber: PropTypes.string,
    secondaryPhoneCountryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    email: PropTypes.string,
    location: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    // Nuevos campos de contacto de emergencia
    emergencyContactName: PropTypes.string,
    emergencyContactPhone: PropTypes.string,
  }).isRequired,
  systemInfo: PropTypes.shape({
    eps: PropTypes.string,
    arl: PropTypes.string,
  }).isRequired,
  onContactInfoChange: PropTypes.func.isRequired,
  onSystemInfoChange: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']).isRequired,
};

export default ContactInfoEdit;